import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { QueryService } from '@app/core/graphql/query/query.service';
import {
    ApplicationActions,
    ApplicationChainActions,
} from '@app/core/ngrx/actions';
import { State } from '@app/core/ngrx/reducers';
import {
    applicationChainSelectors,
    applicationSelectors,
    userSelectors,
} from '@app/core/ngrx/selectors';
import { ApplicationService } from '@app/core/services/application/application.service';
import { reqDocsCheck } from '@app/shared/utils';
import { select, Store } from '@ngrx/store';
import { flatMap, switchMap } from 'rxjs/operators';
import {
    faClinicMedical,
    faUserMd,
    faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { ToolService } from '@app/core/services/tool/tool.service';
import { getState } from '@app/core/ngrx/utils/state';
import { MutationService } from '@app/core/graphql/mutation/mutation.service';
import { ModalService } from '@app/core/services/modal/modal.service';
import { forkJoin, Observable } from 'rxjs';
import { IonContent, NavController } from '@ionic/angular';

interface Profile {
    [key: string]: any;
    chainContractsSigned: boolean;
    immunizations: [];
}

@Component({
    selector: 'app-application-chain',
    templateUrl: './application-chain.page.html',
    styleUrls: ['./application-chain.page.scss'],
})
export class ApplicationChainPage implements OnInit {
    @ViewChild('content') content: IonContent;

    loading = true;

    faClinicMedical = faClinicMedical;
    faUserMd = faUserMd;
    faExclamationCircle = faExclamationCircle;

    segmentView = 'application';

    profile: Profile;
    chainedApplications = [];
    applicationChain$ = this.store.pipe(
        select(applicationChainSelectors.getApplicationChain)
    );
    applicationChainId: string;
    applicationChainState: string;
    applicationChainStates = [];

    selectedApplicationListProgram: any;

    applicationChainEnrollment$: Observable<boolean>;
    checker$ = this.applicationService.enrollmentChecker();
    pendingEnrollment: boolean = false;

    USER_ID$ = this.store.select(userSelectors.getUserID);
    USER_ID: string; //

    loadingProgram = false;
    noChain = false;

    censorContent$ = this.store.pipe(select(userSelectors.blockContent)); // visitor block content if 'unmanaged'

    preApprovalFound: boolean = false;

    constructor(
        private store: Store<State>,
        private route: ActivatedRoute,
        private applicationService: ApplicationService,
        private queryService: QueryService,
        public toolService: ToolService,
        private mutationService: MutationService,
        private modalService: ModalService,
        private navCtrl: NavController,
        private modal: ModalService
    ) {
        this.scrollEvent();
    }

    ngOnInit() {
        this.USER_ID$.subscribe(id => {
            this.USER_ID = id;
        });

        this.applicationChainEnrollment$ = this.store.pipe(
            select(applicationSelectors.getEnroll)
        );

        this.route.params
            .pipe(
                flatMap((query: any) => {
                    const applicationid = query.applicationchainid;
                    return this.applicationService.usershowapplicationchain(
                        applicationid
                    );
                }),
                flatMap((chain: any) => {
                    if (chain.applicationChains.length <= 0) {
                        this.noChain = true;
                        this.loading = false;

                        return forkJoin({});
                    } else {
                        this.chainedApplications =
                            chain.applicationChains[0].Applications;

                        this.store.dispatch(
                            ApplicationChainActions.setApplicationsInChain({
                                applications: [
                                    ...chain.applicationChains[0].Applications,
                                ],
                            })
                        );

                        this.applicationChainId = chain.applicationChains[0].id;

                        this.profile = {
                            ...chain.applicationChains[0].Applications[0].User,
                        };

                        // check in app chain doc list to see immune
                        this.profile.immunizations = [];

                        this.profile.needsInvitation = this.chainedApplications.every(
                            a =>
                                a.hasrequestedinvitation === false &&
                                !this.profile.hasvisa
                        );

                        this.pendingEnrollment = this.chainedApplications.every(
                            a => a.enrollable === true
                        );

                        this.applicationChainStates = this.chainedApplications.map(
                            s => {
                                return s.states;
                            }
                        );

                        this.profile.chainContractsSigned = this.chainedApplications.every(
                            value => value.signedcontract
                        );

                        this.preApprovalFound = this.chainedApplications.some(
                            a => a.Program.preapprovalrequired
                        );

                        if (
                            this.chainedApplications.some(a => !a.madepayment)
                        ) {
                            this.profile.madeApplicationPayment = false;
                        } else {
                            this.profile.madeApplicationPayment = true;
                        }

                        this.applicationChainState = this.applicationChainStates.some(
                            s => s === 'Pending' || s === 'Pending-Booked'
                        )
                            ? 'Pending'
                            : this.applicationChainStates.some(
                                  s => s === 'Accepted'
                              )
                            ? 'Accepted'
                            : this.applicationChainStates.some(
                                  s => s === 'Reserved'
                              )
                            ? 'Reserved'
                            : this.applicationChainStates.some(
                                  s => s === 'Enrolled'
                              )
                            ? 'Enrolled'
                            : this.applicationChainStates.some(
                                  s => s === 'Completed'
                              )
                            ? 'Completed'
                            : 'Pending';

                        return forkJoin({
                            defaultProgram: this.queryService.findProgram(
                                this.chainedApplications[0].programid
                            ),
                            enrollmentCheck: this.queryService.applicationChainEnrollmentCheck(
                                this.applicationChainId
                            ),
                            applicationDocuments: this.queryService.applicationChainApplicationUserDocuments(
                                this.chainedApplications.map(a => a.id)
                            ),
                        });
                    }
                })
            )
            .subscribe(
                ({ defaultProgram, enrollmentCheck, applicationDocuments }) => {
                    this.profile.applicationChainUserDocuments = [
                        ...applicationDocuments,
                    ];

                    this.selectedApplicationListProgram = {
                        ...defaultProgram.data.program,
                    };
                    const canEnroll =
                        enrollmentCheck.data.canEnrollApplications;
                    this.store.dispatch(
                        ApplicationActions.setCanEnroll({ canEnroll })
                    );

                    this.listener();

                    this.loading = false;
                },
                err => {
                    console.log('error in application chain page', err);
                    this.chainedApplications = [];
                    this.profile.chainContractsSigned = false;
                    this.loading = false;
                }
            );
    }

    listener = () => {
        this.checker$
            .pipe(
                switchMap((x: boolean) => {
                    return this.queryService.applicationChainEnrollmentCheck(
                        this.applicationChainId
                    );
                })
            )
            .subscribe((res: any) => {
                const canEnroll = res.data.canEnrollApplications;
                this.store.dispatch(
                    ApplicationActions.setCanEnroll({ canEnroll })
                );
            });
    };

    goToChainPayment() {
        this.navCtrl.navigateRoot(['/payment/'], {
            queryParams: { chainid: this.applicationChainId },
        });
    }

    requestEnrollment() {
        if (!getState(this.store)['application'].canEnroll) {
            this.mutationService
                .chainedEnrollmentChecker(this.applicationChainId.toString())
                .subscribe((res: any) => {
                    this.modalService.enrollmentChecklist(
                        res.data.chainedEnrollmentChecker.missing,
                        res.data.chainedEnrollmentChecker.complete
                    );
                });
        } else {
            this.applicationService
                .chainedRequestEnrollment(this.applicationChainId)
                .subscribe((res: any) => {
                    this.modalService.successfulAction({
                        message:
                            'Congratulations, you have successfully requested enrollment!',
                    });
                    this.pendingEnrollment = true;
                });
        }
    }

    openProgramDetails(ev: CustomEvent) {
        if (ev) {
            this.loadingProgram = true;
            this.queryService.findProgram(ev).subscribe((program: any) => {
                this.selectedApplicationListProgram = null;
                this.selectedApplicationListProgram = {
                    ...program.data.program,
                };
                this.loadingProgram = false;
                this.segmentView = 'program';
            });
        }
    }

    segmentChanged(ev: CustomEvent) {
        this.segmentView = ev.detail.value;
    }

    scrollEvent() {
        this.applicationService
            .getScrollApplicationChainEnrolledSubject()
            .subscribe((res: boolean) => {
                if (res) {
                    this.content.scrollByPoint(0, 750, 600);
                }
            });
    }

    async openSidebar() {
        const applications = this.chainedApplications.map(
            (application: any) => application
        );

        await this.modal.advisorSidebarModal(
            applications,
            applications[0],
            this.profile,
            'dashboard'
        );
    }
}
